import { useState } from 'react';
import { Text } from '@lmn/design-system';
import styled from 'styled-components';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import { Icon } from '@/app/common/icons/Icon';
import { formatCancellationDate, getCancellationBeforeDate } from '../common/dates';
import { getOptionKey } from './roomCommon';
import { hasMealPlanIncludedOptions } from './mealPlanIncludedOptions';
import { MealPlanIncludedOptionsInfoButton } from './MealPlanIncludedOptionsInfoButton';
import { MealPlanIncludedOptionsModal } from './MealPlanIncludedOptionsModal';
import { getPromotionLabel } from './promotionLabel';
import { useTranslation } from '../../translations/Translation';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';

type RoomMealPlanAndCancellationDetailsProps = {
  mealPlans: readonly MealPlanType[];
  selectedMealPlanIndex: number;
  /**
   * The room this summary belongs to. Scopes the info modal's id in the `?modal=` history stack:
   * it has to be the stable room id, because rate ids are replaced by every rooms refetch and an
   * id that changes while the modal is listed leaves an entry nothing can close.
   */
  roomId: string;
};

export function RoomMealPlanAndCancellationDetails(props: RoomMealPlanAndCancellationDetailsProps) {
  const { mealPlans, selectedMealPlanIndex = 0, roomId } = props;
  const { accommodationOnly } = useUrlSearchParams();
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { locale } = localization;
  const { offersData } = useOffersContext();
  const useStructuredTransfer = !!offersData?.transfer;

  const selectedMealPlan = mealPlans[selectedMealPlanIndex];
  const defaultMealPlanAccommodationOnly = selectedMealPlan.boardClass === 'ACCOMODATION_ONLY';

  // MPH-946: the info icon after the meal plan name lists the meals/drinks the plan includes. The
  // modal is only mounted once it has been opened — every eligible room card renders this summary,
  // and OpenXModal runs its hooks (a window popstate listener among them) even while closed.
  const showMealPlanInfo = hasMealPlanIncludedOptions(selectedMealPlan);
  const [isMealPlanInfoModalOpen, setMealPlanInfoModalOpen] = useState<boolean | undefined>();
  const [wasMealPlanInfoOpened, setMealPlanInfoOpened] = useState(false);
  const openMealPlanInfoModal = () => {
    setMealPlanInfoOpened(true);
    setMealPlanInfoModalOpen(true);
  };

  const mealPlanLabel = t(getOptionKey(mealPlans[selectedMealPlanIndex].boardClass, false));
  const mealPlanUpgrade = defaultMealPlanAccommodationOnly
    ? mealPlans.find((mealPlan) => mealPlan.boardClass !== 'ACCOMODATION_ONLY')
    : null;
  const mealPlanUpgradePriceDifference = mealPlanUpgrade
    ? mealPlanUpgrade.finalPrice - mealPlans[selectedMealPlanIndex].finalPrice
    : 0;

  const defaultMealPlanNonRefundable = mealPlans[selectedMealPlanIndex].cancellationPolicy.type === 'NOT_REFUNDABLE';
  const hasRefundableMealPlan =
    defaultMealPlanNonRefundable && mealPlans.some((mealPlan) => mealPlan.cancellationPolicy.type === 'REFUNDABLE');
  const cancellationPolicyLabel = defaultMealPlanNonRefundable
    ? hasRefundableMealPlan
      ? t('vacation.select.room_card.no_refundable_with_option')
      : t('vacation.common.mealplan.room.cancellationtype.nonrefundable.title')
    : mealPlans[selectedMealPlanIndex].cancellationPolicy.upTo
      ? `${accommodationOnly ? t('vacation.common.mealplan.room.cancellationtype.refundable.title') : t('vacation.common.mealplan.room.cancellationtype.refundable.title.dp')} ${t('vacation.common.mealplan.room.cancellationtype.refundable.before', { date: formatCancellationDate(getCancellationBeforeDate(mealPlans[selectedMealPlanIndex].cancellationPolicy.upTo), locale) })}`
      : accommodationOnly
        ? t('vacation.common.mealplan.room.cancellationtype.refundable.title')
        : t('vacation.common.mealplan.room.cancellationtype.refundable.title.dp');

  const defaultMealPlanTransferIncluded = mealPlans[selectedMealPlanIndex].promotions?.find(
    (promotion) => promotion.type === 'TRANSFER_INCLUDED'
  );
  const transferIncludedUpgrade =
    !defaultMealPlanTransferIncluded &&
    mealPlans.some((mealPlan) => mealPlan.promotions?.some((promotion) => promotion.type === 'TRANSFER_INCLUDED'));

  const extraPromotions = mealPlans[selectedMealPlanIndex]?.promotions?.filter(
    (promotion) => !['TRANSFER_INCLUDED', 'FLASH_SALE'].includes(promotion.type.toUpperCase())
  );

  return (
    <Container>
      <DetailInfoRow>
        <Icon category="Food and beverages" name="icon-breakfast" size={16} color="iconPrimary" alt="meal plan" />
        <Text variant="BodySmall">
          {mealPlanUpgradePriceDifference
            ? `${mealPlanLabel} - ${t('vacation.select.room_card.room_only_with_option', { amount: formatPrice({ price: mealPlanUpgradePriceDifference, localization }) })}`
            : mealPlanLabel}
          {showMealPlanInfo ? <MealPlanIncludedOptionsInfoButton onClick={openMealPlanInfoModal} /> : null}
        </Text>
      </DetailInfoRow>
      <DetailInfoRow>
        {defaultMealPlanNonRefundable && !hasRefundableMealPlan ? (
          <Icon
            category="Security and safety"
            name="icon-slashed-shield"
            size={16}
            color="iconPrimary"
            alt="Non Refundable"
          />
        ) : (
          <Icon
            category="Security and safety"
            name="icon-shield"
            size={16}
            color="iconPrimary"
            alt="Cancellation option available"
          />
        )}
        <Text variant="BodySmall">{cancellationPolicyLabel}</Text>
      </DetailInfoRow>
      {useStructuredTransfer || defaultMealPlanTransferIncluded || transferIncludedUpgrade ? (
        <DetailInfoRow>
          <Icon category="Location" name="icon-signpost" size={16} color="iconPrimary" alt="transfer options" />
          <Text variant="BodySmall">
            {useStructuredTransfer
              ? offersData?.transfer?.inbound.key
                ? t(offersData.transfer.inbound.key)
                : ''
              : defaultMealPlanTransferIncluded
                ? `${defaultMealPlanTransferIncluded.label}`
                : t('vacation.select.room_card.transfer_available')}
          </Text>
        </DetailInfoRow>
      ) : undefined}
      {extraPromotions?.map((promotion) => (
        <DetailInfoRow key={promotion.label}>
          <Icon
            category="UI Essentials"
            name="icon-bullet"
            size={32}
            footprint={16}
            color="iconPrimary"
            alt={getPromotionLabel(promotion, t)}
          />
          <Text variant="BodySmall">{getPromotionLabel(promotion, t) || promotion.description}</Text>
        </DetailInfoRow>
      ))}
      {showMealPlanInfo && wasMealPlanInfoOpened ? (
        <MealPlanIncludedOptionsModal
          id={`room-card-${roomId}`}
          mealPlan={selectedMealPlan}
          isOpen={isMealPlanInfoModalOpen}
          onDismiss={() => setMealPlanInfoModalOpen(false)}
          onOpenByBrowserHistory={() => setMealPlanInfoModalOpen(true)}
        />
      ) : null}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DetailInfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 2px 0;
`;
